import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { postRequest, getRequest } from '../../../lib/api';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { AuthContext } from '../../../authContext/context';
import { API_BASE, API_BASE_URL, CUSTOMER_PORTAL_URL } from '../../../lib/config';
import Swal from 'sweetalert2';
import Notes from '../Notes/note';
import ViewQuoteDetailModal from '../../../components/ViewQuoteDetailModal';
import jsPDF from 'jspdf';

// Revise Quote Modal Component for Mortgage Redemption
function ReviseQuoteModal({ show, onHide, quoteFeeData, leadData, authToken, onReviseQuote, legalFeeData }) {
  const [fees, setFees] = useState({
    legal_fee: 0,
    leasehold_fee: 0,
    tt_fee: 0,
    identity_check: 0,
    official_copies: 0
  });
  const [totals, setTotals] = useState({
    net: 0,
    vat: 0,
    gross: 0,
    legalCost: { net: 0, vat: 0, gross: 0 },
    disbursementCost: { net: 0, vat: 0, gross: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize with quote fee data
  React.useEffect(() => {
    if (show && quoteFeeData && quoteFeeData.fees) {
      const qf = quoteFeeData.fees;
      const qd = quoteFeeData.disbursements || {};
      const initialFees = {
        legal_fee: qf.legalFee?.amount || 0,
        leasehold_fee: qf.leaseholdFee?.amount || 0,
        tt_fee: qf.telegraphicTransferFee?.amount || 0,
        identity_check: qf.identityCheck?.amount || 0,
        official_copies: qd.officialCopies?.amount || 0
      };
      setFees(initialFees);
      calculateTotals(initialFees);
    }
  }, [quoteFeeData, show]);

  // Calculate totals
  const calculateTotals = (currentFees) => {
    // Editable fees with VAT (Legal, Leasehold, TT)
    const legalFeeNet = Number(currentFees.legal_fee) || 0;
    const legalFeeVAT = legalFeeNet * 0.20;

    const leaseholdFeeNet = Number(currentFees.leasehold_fee) || 0;
    const leaseholdFeeVAT = leaseholdFeeNet * 0.20;

    const ttFeeNet = Number(currentFees.tt_fee) || 0;
    const ttFeeVAT = ttFeeNet * 0.20;

    // Non-editable fees with VAT (Identity Check)
    const identityCheckNet = Number(currentFees.identity_check) || 0;
    const identityCheckVAT = identityCheckNet * 0.20;

    // Non-editable disbursement (no VAT - Official Copies)
    const officialCopiesNet = Number(currentFees.official_copies) || 0;

    // Calculate Legal Cost (all fees before disbursements)
    const legalCostNet = legalFeeNet + leaseholdFeeNet + ttFeeNet + identityCheckNet;
    const legalCostVAT = legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + identityCheckVAT;
    const legalCostGross = legalCostNet + legalCostVAT;

    // Calculate Disbursement Cost (Third Party Expenditure)
    const disbursementCostNet = officialCopiesNet;
    const disbursementCostVAT = 0; // No VAT on official copies
    const disbursementCostGross = disbursementCostNet + disbursementCostVAT;

    // Calculate Grand Totals
    const totalNet = legalCostNet + disbursementCostNet;
    const totalVAT = legalCostVAT + disbursementCostVAT;
    const totalGross = totalNet + totalVAT;

    setTotals({
      net: totalNet,
      vat: totalVAT,
      gross: totalGross,
      legalCost: { net: legalCostNet, vat: legalCostVAT, gross: legalCostGross },
      disbursementCost: { net: disbursementCostNet, vat: disbursementCostVAT, gross: disbursementCostGross }
    });
  };

  const handleReviseQuote = async () => {
    try {
      setIsLoading(true);

      const payload = {
        lead_id: leadData.lead_id || leadData._id,
        legal_fee: Number(fees.legal_fee),
        vat_amount: totals.vat,
        total_quote: totals.gross,
        fees: {
          legal_fee: Number(fees.legal_fee),
          leasehold_fee: Number(fees.leasehold_fee),
          tt_fee: Number(fees.tt_fee),
          identity_check: Number(fees.identity_check),
          official_copies: Number(fees.official_copies)
        },
        branch_id: legalFeeData?.branch_id || leadData?.branch_id,
        property_value: leadData?.property_value
      };

      console.log('📝 Modal submitting revised quote for Mortgage Redemption:', payload);

      await onReviseQuote(payload);
      onHide();
    } catch (error) {
      console.error('❌ Modal error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '600' }}>Revise Quote</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '0 20px 20px 20px' }}>
        <div style={{ overflowX: 'auto' }}>
          <Table bordered hover style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', padding: '12px 15px', borderRight: '2px solid #dee2e6', width: '40%' }}>
                  Mortgage Redemption <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Net</th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>VAT</th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Gross</th>
              </tr>
            </thead>
            <tbody>
              {/* Legal Fees - Editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Legal Fees</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ width: '140px', margin: '0 auto', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>£</span>
                    <input
                      type="number"
                      value={fees.legal_fee}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFees(prev => ({ ...prev, legal_fee: newValue }));
                        calculateTotals({ ...fees, legal_fee: newValue });
                      }}
                      style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(Number(fees.legal_fee) * 0.20).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(Number(fees.legal_fee) * 1.20).toFixed(2)}
                </td>
              </tr>

              {/* Fixed Fees Section Header */}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan="4" style={{ padding: '10px 15px', fontWeight: '600' }}>
                  Fixed Fees <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </td>
              </tr>

              {/* Leasehold Fee - Editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Leasehold Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ width: '140px', margin: '0 auto', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>£</span>
                    <input
                      type="number"
                      value={fees.leasehold_fee}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFees(prev => ({ ...prev, leasehold_fee: newValue }));
                        calculateTotals({ ...fees, leasehold_fee: newValue });
                      }}
                      style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(Number(fees.leasehold_fee) * 0.20).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(Number(fees.leasehold_fee) * 1.20).toFixed(2)}
                </td>
              </tr>

              {/* Telegraphic Transfer (TT) Fee - Editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Telegraphic Transfer (TT) Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ width: '140px', margin: '0 auto', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>£</span>
                    <input
                      type="number"
                      value={fees.tt_fee}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFees(prev => ({ ...prev, tt_fee: newValue }));
                        calculateTotals({ ...fees, tt_fee: newValue });
                      }}
                      style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(Number(fees.tt_fee) * 0.20).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(Number(fees.tt_fee) * 1.20).toFixed(2)}
                </td>
              </tr>

              {/* Identity Check - Non-editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Identity Check</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{Number(fees.identity_check).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(Number(fees.identity_check) * 0.20).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(Number(fees.identity_check) * 1.20).toFixed(2)}
                </td>
              </tr>

              {/* Disbursements Section Header */}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan="4" style={{ padding: '10px 15px', fontWeight: '600' }}>
                  Disbursements <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </td>
              </tr>

              {/* Official Copies - Non-editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Official Copies</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{Number(fees.official_copies).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £0.00
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{Number(fees.official_copies).toFixed(2)}
                </td>
              </tr>

              {/* TOTAL LEGAL COST */}
              <tr style={{ backgroundColor: '#fff3cd', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>TOTAL LEGAL COST</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.gross.toFixed(2)}</td>
              </tr>

              {/* TOTAL DISBURSEMENT COST */}
              <tr style={{ backgroundColor: '#d1ecf1', fontWeight: '600' }}>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>TOTAL DISBURSEMENT COST</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.gross.toFixed(2)}</td>
              </tr>

              {/* Total (Legal cost + disbursement) */}
              <tr style={{ backgroundColor: '#d4edda', fontWeight: '700', fontSize: '1rem', borderTop: '2px solid #28a745' }}>
                <td style={{ padding: '15px', borderRight: '2px solid #dee2e6' }}>Total (Legal cost + disbursement)</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.gross.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', padding: '0 20px 20px 20px', backgroundColor: '#f8f9fa' }}>
        <Button variant="outline-secondary" onClick={onHide} style={{ paddingLeft: '20px', paddingRight: '20px', fontWeight: '500' }}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleReviseQuote} disabled={isLoading} style={{ paddingLeft: '25px', paddingRight: '25px', fontWeight: '500' }}>
          {isLoading ? 'Updating...' : 'Revise Quote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const ViewLeadMortgageRedemption = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteFeeData, setQuoteFeeData] = useState({
    ownership_type: null,
    branch_name: '',
    fees: {},
    net: 0,
    vat: 0,
    gross: 0
  });
  const [loadingFee, setLoadingFee] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [customerDetails, setCustomerDetails] = useState([]);
  const [reviseModalShow, setReviseModalShow] = useState(false);
  const [legalFeeData, setLegalFeeData] = useState(null);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [revisionCount, setRevisionCount] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetailModal, setShowQuoteDetailModal] = useState(false);

  // Communication tab states
  const [contactAttempts, setContactAttempts] = useState(0);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [viewEmailModal, setViewEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    if (lead_id && authCtx.token) {
      fetchLeadData();
      fetchLenderPanels();
    }
  }, [lead_id, authCtx.token]);

  // Fetch branch data when leadData is loaded
  useEffect(() => {
    if (leadData && authCtx.token) {
      fetchQuoteFee();
      fetchQuoteHistory();
    }
  }, [leadData, authCtx.token]);

  // Fetch customer details when leadData is loaded
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      console.log('🔄 fetchCustomerDetails called');
      console.log('📦 leadData:', leadData);
      console.log('👥 leadData.customers:', leadData?.customers);

      if (!leadData?.customers) {
        console.log('⚠️ No customers field in leadData');
        setCustomerDetails([]); // Set empty array to stop showing "Loading..."
        return;
      }

      if (leadData.customers.length === 0) {
        console.log('⚠️ Customers array is empty');
        setCustomerDetails([]); // Set empty array to stop showing "Loading..."
        return;
      }

      try {
        console.log('🔍 Fetching customer details for IDs:', leadData.customers);
        console.log('🔍 Number of customers to fetch:', leadData.customers.length);

        const customerPromises = leadData.customers.map(async (customerId, index) => {
          console.log(`🔍 Processing customer ${index}:`, customerId, 'Type:', typeof customerId);

          // If it's already an object with name, return it
          if (typeof customerId === 'object' && customerId.forename) {
            console.log('✅ Customer already populated:', customerId);
            return customerId;
          }

          // If it's just an ID, fetch details
          const customerIdString = typeof customerId === 'string' ? customerId : customerId._id || customerId;
          console.log(`📤 Fetching customer details for ID: ${customerIdString}`);

          try {
            const res = await postRequest(
              `private/getCustomerIndividualData/${customerIdString}`,
              {},
              authCtx.token
            );

            console.log(`📡 API Response for ${customerIdString}:`, res);

            if (res?.data) {
              console.log('✅ Fetched customer:', res.data);
              return res.data;
            }
            console.log('⚠️ No data in response for:', customerIdString);
            return null;
          } catch (err) {
            console.error('❌ Error fetching customer:', customerIdString, err);
            return null;
          }
        });

        console.log('⏳ Waiting for all customer promises...');
        const customers = await Promise.all(customerPromises);
        console.log('📥 All promises resolved:', customers);

        const validCustomers = customers.filter(c => c !== null);
        console.log('✅ Valid customers after filtering:', validCustomers);

        setCustomerDetails(validCustomers);
        console.log('✅ Customer details state updated');
      } catch (err) {
        console.error('❌ Error fetching customer details:', err);
        setCustomerDetails([]); // Set empty array on error
      }
    };

    if (leadData && authCtx.token) {
      console.log('🚀 Starting customer details fetch');
      fetchCustomerDetails();
    } else {
      console.log('⚠️ Cannot fetch customers - leadData or token missing');
    }
  }, [leadData, authCtx.token]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching Mortgage Redemption lead data for:', lead_id);

      // Use get-single-lead API
      const res = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);

      if ((res?.status === 200 || res?.status === 'success') && res?.data) {
        const data = res.data;
        setLeadData(data);

        // Initialize quote history and revision count
        setQuoteHistory(data.quote_history || []);
        setRevisionCount(data.revision_count || 0);

        // Initialize contact attempts
        setContactAttempts(data.contact_attempts || 0);

        console.log('✅ Lead data loaded:', data);
        console.log('🏠 Address:', data.address || data.property_address);
        console.log('💰 Property Value:', data.property_value);
        console.log('👥 Customers Array:', data.customers);
        console.log('👥 Customers Length:', data.customers?.length);
        console.log('👥 First Customer:', data.customers?.[0]);
        console.log('👥 Customer Type:', typeof data.customers?.[0]);
        console.log('🏦 Lender Panel:', data.lender_panel_id);
        console.log('📦 Conveyancing Details:', data.conveyancing_details);
        console.log('📋 Full Data Structure:', JSON.stringify(data, null, 2));
      } else {
        console.log('⚠️ No lead data found');
      }
    } catch (err) {
      console.error('❌ Error fetching lead data:', err);
      Swal.fire('Error', 'Failed to load lead data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
        console.log('✅ Lender panels loaded:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching lender panels:', error);
    }
  };

  const fetchQuoteFee = async () => {
    try {
      setLoadingFee(true);
      console.log('🔍 Fetching Mortgage Redemption fee for:', lead_id);

      if (!leadData) {
        console.log('⚠️ No lead data available');
        return;
      }

      console.log('📞 Fetching calculation using fetch_lead_calculations API...');

      // Get partner_id if exists
      const partnerId = leadData.partner_id?._id || leadData.partner_id;
      console.log('🤝 Partner ID for fetch:', partnerId);

      // ✅ Use same API as other service types - fetch from database
      const API_BASE_URL = `${API_BASE}/v1/admin/`;
      const fetchUrl = partnerId
        ? `${API_BASE_URL}private/fetch_lead_calculations/${lead_id}?partner_id=${partnerId}`
        : `${API_BASE_URL}private/fetch_lead_calculations/${lead_id}`;

      console.log('📞 Fetching from:', fetchUrl);

      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      const res = await response.json();

      console.log('📡 API Response:', res);

      if (res?.status === 200 && res?.data) {
        console.log('✅ Calculation data loaded from database:', res.data);

        // ✅ Use pre-calculated totals from database
        const totals = res.data.totals || { net: 0, vat: 0, gross: 0 };

        console.log('💰 Database totals:', totals);
        console.log('📋 Fees from API:', res.data.fees);
        console.log('📋 Disbursements from API:', res.data.disbursements);

        // Calculate totals from individual fee items if database totals are 0
        let calculatedNet = totals.net || 0;
        let calculatedVAT = totals.vat || 0;
        let calculatedGross = totals.gross || 0;

        if (calculatedGross === 0 && res.data.fees) {
          // Sum up all fees
          const legalFeeAmount = res.data.fees.legalFee?.amount || 0;
          const legalFeeVAT = res.data.fees.legalFee?.vat || 0;
          const legalFeeGross = res.data.fees.legalFee?.gross || 0;

          const leaseholdFeeAmount = res.data.fees.leaseholdFee?.amount || 0;
          const leaseholdFeeVAT = res.data.fees.leaseholdFee?.vat || 0;
          const leaseholdFeeGross = res.data.fees.leaseholdFee?.gross || 0;

          const ttFeeAmount = res.data.fees.telegraphicTransferFee?.amount || 0;
          const ttFeeVAT = res.data.fees.telegraphicTransferFee?.vat || 0;
          const ttFeeGross = res.data.fees.telegraphicTransferFee?.gross || 0;

          const idCheckAmount = res.data.fees.identityCheck?.amount || 0;
          const idCheckVAT = res.data.fees.identityCheck?.vat || 0;
          const idCheckGross = res.data.fees.identityCheck?.gross || 0;

          // Sum up disbursements
          const officialCopiesAmount = res.data.disbursements?.officialCopies?.amount || 0;
          const officialCopiesVAT = res.data.disbursements?.officialCopies?.vat || 0;
          const officialCopiesGross = res.data.disbursements?.officialCopies?.gross || 0;

          // Calculate grand totals
          calculatedNet = legalFeeAmount + leaseholdFeeAmount + ttFeeAmount + idCheckAmount + officialCopiesAmount;
          calculatedVAT = legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + idCheckVAT + officialCopiesVAT;
          calculatedGross = legalFeeGross + leaseholdFeeGross + ttFeeGross + idCheckGross + officialCopiesGross;

          console.log('💰 Calculated totals from individual items:', { calculatedNet, calculatedVAT, calculatedGross });
        }

        // ✅ Use fees and disbursements directly from API (already in correct format with camelCase keys)
        setQuoteFeeData({
          ownership_type: res.data.ownership_type,
          branch_name: res.data.branch_name || '',
          fees: res.data.fees || {},
          disbursements: res.data.disbursements || {},
          net: calculatedNet,
          vat: calculatedVAT,
          gross: calculatedGross
        });

        // Set legalFeeData for revise quote modal
        // NOTE: official_copies is in disbursements, not fees
        setLegalFeeData({
          legal_fee: res.data.fees.legalFee?.amount || 0,
          leasehold_fee: res.data.fees.leaseholdFee?.amount || 0,
          tt_fee: res.data.fees.telegraphicTransferFee?.amount || 0,
          identity_check: res.data.fees.identityCheck?.amount || 0,
          official_copies: res.data.disbursements?.officialCopies?.amount || 0,
          branch_name: res.data.branch_name,
          branch_id: res.data.branch_id
        });
      } else {
        throw new Error(res?.message || 'Failed to fetch quote details');
      }

    } catch (err) {
      console.error('❌ Error fetching fee data:', err);
      Swal.fire('Error', err?.message || 'Failed to fetch quote details', 'error');
    } finally {
      setLoadingFee(false);
    }
  };

  const downloadQuotePDF = async () => {
    try {
      // Fetch data if not available
      let pdfData = quoteFeeData;
      if (!pdfData) {
        console.log('📥 No quote data available, fetching...');
        await fetchQuoteFee();
        pdfData = quoteFeeData;

        if (!pdfData) {
          Swal.fire('Error', 'No quote data available to download', 'error');
          return;
        }
      }

      console.log('📄 Generating PDF with quote data:', pdfData);

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = 20;

      // Quote #
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Quote #`, margin, yPosition);
      yPosition += 6;

      // Lead ID
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Lead ID: L-${lead_id}`, margin, yPosition);
      yPosition += 10;

      // Main Title - Quote for Mortgage Redemption
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Quote for Mortgage Redemption', margin, yPosition);
      yPosition += 15;

      // Description paragraph
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const description = `Thank you for requesting a conveyancing quote from My Law Matters, you will find a complete breakdown of our costs below. We would be delighted to act for you and one of our team will contact you shortly. In the meantime if you have any questions at all then please do get in touch on 0330 124 2171.`;
      const splitDescription = doc.splitTextToSize(description, pageWidth - (margin * 2));
      doc.text(splitDescription, margin, yPosition);
      yPosition += 25;

      // Section Header - MORTGAGE REDEMPTION (in blue)
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(41, 128, 185); // Blue color
      doc.text('MORTGAGE REDEMPTION', margin, yPosition);
      yPosition += 10;

      // Total Cost Summary
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Cost: £${pdfData.gross.toFixed(2)} (£${pdfData.net.toFixed(2)} + £${pdfData.vat.toFixed(2)} VAT)`, margin, yPosition);
      yPosition += 15;

      // Format currency helper
      const formatCurrency = (value) => `£${(value || 0).toFixed(2)}`;

      // Table drawing function (same as Sale/Purchase)
      const drawTable = (data, headers) => {
        const colWidths = [90, 30, 30, 30];
        let xPos = margin;

        // Draw headers
        doc.setFillColor(240, 240, 240);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);

        let headerXPos = margin;
        headers.forEach((header, i) => {
          // Draw header cell background and border
          doc.setFillColor(240, 240, 240);
          doc.rect(headerXPos, yPosition, colWidths[i], 8, 'F');
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.1);
          doc.rect(headerXPos, yPosition, colWidths[i], 8, 'S');

          // Draw header text
          doc.setTextColor(0, 0, 0);
          doc.text(header, headerXPos + 2, yPosition + 5);
          headerXPos += colWidths[i];
        });
        yPosition += 10;

        // Draw rows
        doc.setFont('helvetica', 'normal');
        data.forEach(row => {
          xPos = margin;

          // Set font based on row type
          if (row.isGrandTotal) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
          } else if (row.isHeader) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
          } else {
            doc.setTextColor(0, 0, 0);
          }

          // Draw cell borders and content
          row.cells.forEach((cell, i) => {
            // Draw cell with background fill and border
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.1);

            if (row.isGrandTotal) {
              doc.setFillColor(240, 240, 240);
              doc.rect(xPos, yPosition, colWidths[i], 8, 'FD');
            } else if (row.isHeader) {
              doc.setFillColor(240, 240, 240);
              doc.rect(xPos, yPosition, colWidths[i], 8, 'FD');
            } else {
              doc.rect(xPos, yPosition, colWidths[i], 8, 'S');
            }

            const text = String(cell || '');
            if (i > 0 && !row.isHeader && !row.isGrandTotal) {
              // Right align numbers
              const textWidth = doc.getTextWidth(text);
              doc.text(text, xPos + colWidths[i] - textWidth - 2, yPosition + 5);
            } else {
              doc.text(text, xPos + 2, yPosition + 5);
            }
            xPos += colWidths[i];
          });

          // Reset styling after row
          if (row.isHeader || row.isGrandTotal) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
          }
          yPosition += 8;
        });
      };

      // Create table data
      const tableData = [
        { cells: ['Legal Cost', '', '', ''], isHeader: true }
      ];

      // Legal Fees
      if (pdfData.fees?.legalFee) {
        tableData.push({
          cells: [
            'Legal Fees',
            formatCurrency(pdfData.fees.legalFee.amount),
            formatCurrency(pdfData.fees.legalFee.vat),
            formatCurrency(pdfData.fees.legalFee.gross)
          ]
        });
      }

      // Leasehold Fee
      if (pdfData.fees?.leaseholdFee) {
        tableData.push({
          cells: [
            'Leasehold Fee',
            formatCurrency(pdfData.fees.leaseholdFee.amount),
            formatCurrency(pdfData.fees.leaseholdFee.vat),
            formatCurrency(pdfData.fees.leaseholdFee.gross)
          ]
        });
      }

      // TT Fee
      if (pdfData.fees?.telegraphicTransferFee) {
        tableData.push({
          cells: [
            'Telegraphic Transfer (TT) Fee',
            formatCurrency(pdfData.fees.telegraphicTransferFee.amount),
            formatCurrency(pdfData.fees.telegraphicTransferFee.vat),
            formatCurrency(pdfData.fees.telegraphicTransferFee.gross)
          ]
        });
      }

      // Identity Check
      if (pdfData.fees?.identityCheck) {
        tableData.push({
          cells: [
            'Identity Check',
            formatCurrency(pdfData.fees.identityCheck.amount),
            formatCurrency(pdfData.fees.identityCheck.vat),
            formatCurrency(pdfData.fees.identityCheck.gross)
          ]
        });
      }

      // Disbursements Section
      tableData.push({ cells: ['Disbursements', '', '', ''], isHeader: true });

      // Official Copies
      if (pdfData.disbursements?.officialCopies) {
        tableData.push({
          cells: [
            'Official Copies',
            formatCurrency(pdfData.disbursements.officialCopies.amount),
            formatCurrency(pdfData.disbursements.officialCopies.vat),
            formatCurrency(pdfData.disbursements.officialCopies.gross)
          ]
        });
      }

      // Empty row for spacing
      tableData.push({ cells: ['', '', '', ''] });

      // Grand Total Row
      tableData.push({
        cells: [
          'TOTAL (Legal Cost + Disbursement Cost)',
          formatCurrency(pdfData.net),
          formatCurrency(pdfData.vat),
          formatCurrency(pdfData.gross)
        ],
        isGrandTotal: true
      });

      // Draw the table
      drawTable(tableData, ['Description', 'Net', 'VAT', 'Gross']);

      // Save the PDF
      doc.save(`Mortgage_Redemption_Quote_L${lead_id}.pdf`);

      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      Swal.fire('Error', 'Failed to generate PDF', 'error');
    }
  };

  const handleDeleteLead = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this lead #${lead_id}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        console.log('Attempting to delete lead with ID:', lead_id);

        // Use the DELETE method with fetch API
        const response = await fetch(`/api/v1/admin/private/delete-lead/${lead_id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authCtx.token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Delete response status:', response.status);

        let res;
        if (response.ok) {
          res = await response.json();
          console.log('Delete response data:', res);
        } else {
          // If the specific delete endpoint fails, log the error
          const errorText = await response.text();
          console.error('Delete failed with status:', response.status, 'Error:', errorText);
          throw new Error(`Delete failed with status ${response.status}`);
        }

        if (res && (res.status === 200 || res.status === 'success')) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'The lead has been deleted successfully.',
            timer: 1500,
            showConfirmButton: false
          });

          // Navigate back to leads list after a short delay
          setTimeout(() => {
            navigate('/leads');
          }, 1500);
        } else {
          throw new Error(res?.message || 'Failed to delete lead');
        }
      } catch (err) {
        console.error('Error deleting lead:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'An error occurred while deleting the lead.'
        });
      }
    }
  };

  // Handle revise quote
  const handleReviseQuote = async (payload) => {
    try {
      console.log('🔄 ===== REVISE QUOTE (NEW API) =====');
      console.log('📦 Received payload from modal:', payload);

      // 🎯 Transform flat fees to NESTED format (Mortgage Redemption specific)
      const nestedFees = {
        legalFee: {
          amount: Number(payload.fees.legal_fee || 0),
          vat: Number(payload.fees.legal_fee || 0) * 0.20,
          gross: Number(payload.fees.legal_fee || 0) * 1.20,
          label: 'Legal Fee'
        },
        leaseholdFee: {
          amount: Number(payload.fees.leasehold_fee || 0),
          vat: Number(payload.fees.leasehold_fee || 0) * 0.20,
          gross: Number(payload.fees.leasehold_fee || 0) * 1.20,
          label: 'Leasehold Fee'
        },
        telegraphicTransferFee: {
          amount: Number(payload.fees.tt_fee || 0),
          vat: Number(payload.fees.tt_fee || 0) * 0.20,
          gross: Number(payload.fees.tt_fee || 0) * 1.20,
          label: 'Telegraphic Transfer Fee'
        },
        identityCheck: {
          amount: Number(payload.fees.identity_check || 0),
          vat: Number(payload.fees.identity_check || 0) * 0.20,
          gross: Number(payload.fees.identity_check || 0) * 1.20,
          label: 'Identity Check'
        }
      };

      const nestedDisbursements = {
        officialCopies: {
          amount: Number(payload.fees.official_copies || 0),
          vat: 0,
          gross: Number(payload.fees.official_copies || 0),
          label: 'Official Copies'
        }
      };

      // Calculate Legal Cost Total
      const legalCostNet = Object.values(nestedFees).reduce((sum, fee) => sum + fee.amount, 0);
      const legalCostVat = Object.values(nestedFees).reduce((sum, fee) => sum + fee.vat, 0);
      const legalCostGross = Object.values(nestedFees).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Disbursement Cost Total
      const disbursementCostNet = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.amount, 0);
      const disbursementCostVat = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.vat, 0);
      const disbursementCostGross = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Grand Total
      const totalNet = legalCostNet + disbursementCostNet;
      const totalVat = legalCostVat + disbursementCostVat;
      const totalGross = legalCostGross + disbursementCostGross;

      const nestedTotals = {
        legalCost: { net: legalCostNet, vat: legalCostVat, gross: legalCostGross },
        disbursementCost: { net: disbursementCostNet, vat: disbursementCostVat, gross: disbursementCostGross },
        total: { net: totalNet, vat: totalVat, gross: totalGross }
      };

      console.log('✅ Nested format created:', { nestedFees, nestedDisbursements, nestedTotals });

      // 🔄 Call NEW reviseQuote API
      const apiUrl = `${API_BASE_URL}private/reviseQuote/${lead_id}`;
      console.log('📡 Calling NEW reviseQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        },
        body: JSON.stringify({
          fees: nestedFees,
          disbursements: nestedDisbursements,
          totals: nestedTotals
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Revise Quote API Response:', result);

      if (result?.status === 200) {
        console.log('✅ Quote revised successfully!');

        // 📚 Fetch fresh quote history from database
        await fetchQuoteHistory();

        // 🔄 Refresh active quote
        await fetchQuoteFee();

        Swal.fire('Success', 'Quote revised successfully!', 'success');

        // Close the revise quote modal
        setReviseModalShow(false);
      } else {
        Swal.fire('Error', result?.message || 'Failed to revise quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error revising quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // 🔁 Handle Regenerate Quote - Restore Original Quote
  const handleRegenerateQuote = async () => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Regenerate Original Quote?',
        text: 'This will restore the original quote values. Current changes will be saved to history.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, regenerate it!'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      console.log('🔁 ===== REGENERATE QUOTE =====');
      console.log('📋 Lead ID:', lead_id);

      // Call regenerateQuote API
      const apiUrl = `${API_BASE_URL}private/regenerateQuote/${lead_id}`;
      console.log('📡 Calling regenerateQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Regenerate Quote API Response:', result);

      if (result.status === 200) {
        // Refresh data
        await fetchQuoteHistory();
        await fetchQuoteFee();

        Swal.fire('Success', 'Original quote restored successfully!', 'success');
      } else {
        Swal.fire('Error', result?.message || 'Failed to regenerate quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error regenerating quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // 📚 Fetch Quote History from Database
  const fetchQuoteHistory = async () => {
    try {
      console.log('📚 Fetching quote history from database...');

      const response = await fetch(`${API_BASE_URL}private/getQuoteHistory/${lead_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      const result = await response.json();

      if (result.status === 200 && result.data) {
        console.log('✅ Quote history fetched:', result.data);
        setQuoteHistory(result.data);
      } else {
        console.warn('⚠️ No quote history found');
        setQuoteHistory([]);
      }
    } catch (error) {
      console.error('❌ Error fetching quote history:', error);
      setQuoteHistory([]);
    }
  };

  const slugify = (text) =>
    text
      ?.toLowerCase()
      ?.replace(/ /g, '-')
      ?.replace(/[^\w-]+/g, '')
      ?.replace(/-+/g, '-');

  // Communication Tab Functions
  const handleUpdateContactAttempt = async (type) => {
    try {
      let updatedCount;
      if (type === 'increase') {
        updatedCount = contactAttempts + 1;
      } else if (type === 'reset') {
        updatedCount = 0;
      } else {
        updatedCount = Math.max(0, contactAttempts - 1);
      }

      const res = await postRequest(
        'private/change-contact-status',
        {
          lead_id: lead_id,
          contact_attempts: updatedCount,
        },
        authCtx.token
      );

      if (res?.status === 200 || res?.status === 'success') {
        setContactAttempts(updatedCount);
        const message = type === 'increase' ? 'increased' : type === 'reset' ? 'reset' : 'decreased';
        Swal.fire('Success', `Contact attempt ${message}`, 'success');
      } else {
        throw new Error(res?.message || 'Failed to update contact attempt');
      }
    } catch (err) {
      console.error('Error updating contact attempt:', err);
      Swal.fire('Error', err?.message || 'Failed to update contact attempt', 'error');
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      console.log('📧 === FETCHING EMAIL TEMPLATES (Mortgage Redemption) ===');
      console.log('Lead data product_id:', leadData?.product_id);

      // Get product ID from leadData
      let productId = leadData?.product_id?._id || leadData?.product_id;
      console.log('🔍 Product ID:', productId);

      // If productId is not available, try to fetch it from products API
      if (!productId) {
        console.log('⚠️ Product ID not found, fetching products...');
        try {
          const productsRes = await postRequest('private/getAllProducts', {}, authCtx.token);
          if (productsRes?.data) {
            const conveyancingProduct = productsRes.data.find(p =>
              p.name?.toLowerCase().includes('conveyancing') ||
              p.product_name?.toLowerCase().includes('conveyancing')
            );
            if (conveyancingProduct) {
              productId = conveyancingProduct._id || conveyancingProduct.product_id;
              console.log('✅ Found Conveyancing product ID:', productId);
            }
          }
        } catch (prodErr) {
          console.log('❌ Error fetching products:', prodErr);
        }
      }

      // Fetch automation actions for this product
      const automationUrl = `${API_BASE}/v1/admin/automationList`;
      const requestBody = {
        productId: productId,
        page: 1,
        limit: 100
      };

      const response = await fetch(automationUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      console.log('📡 Automations response:', responseData);

      if (response.ok && (responseData?.status === 200 || responseData?.status === 'success')) {
        const automations = responseData.data?.docs || responseData.data || [];
        console.log('✅ Automations found:', automations.length);

        // Extract email templates from automation actions
        const emailTemplates = [];

        automations.forEach((automation) => {
          // Check if automation has effect with "Send Email"
          if (automation.effect && automation.effect.type?.toLowerCase().includes('send email')) {
            console.log('   ✅ Found Send Email effect:', automation.effect.type);

            const template = {
              _id: automation.effect.emailTemplateId?._id || automation._id,
              name: automation.effect.emailTemplateId?.name || 'Unnamed Template',
              template_id: automation.effect.emailTemplateId?._id,
              automation_id: automation._id,
              effect: automation.effect
            };

            emailTemplates.push(template);
            console.log('   ✅ Added email template:', template.name);
          }
        });

        console.log('📧 Total email templates extracted:', emailTemplates.length);
        setEmailTemplates(emailTemplates);
      } else {
        console.log('⚠️ No automations found');
        setEmailTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching email templates:', error);
      setEmailTemplates([]);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      setLoadingEmails(true);
      const emailLogsResponse = await postRequest(
        'private/getEmailLogs',
        {
          lead_id: lead_id,
          page: 1,
          limit: 100
        },
        authCtx.token
      );

      if (emailLogsResponse?.status === 200 && emailLogsResponse?.data) {
        setEmailLogs(emailLogsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setLoadingEmails(false);
    }
  };

  // Generate dynamic fee table for Mortgage Redemption email templates
  const generateMortgageRedemptionFeeTable = (leadDataParam, legalFeeDataParam) => {
    const useLegalFeeData = legalFeeDataParam || leadDataParam;
    const ownershipType = leadDataParam?.ownership_type || 'Freehold';

    // Format currency helper
    const formatCurrency = (amount) => {
      const value = Number(amount) || 0;
      return `£${value.toFixed(2)}`;
    };

    // Legal Fee
    const legalFeeNet = Number(useLegalFeeData.legal_fee || 0);
    const legalFeeVAT = legalFeeNet * 0.20;
    const legalFeeGross = legalFeeNet + legalFeeVAT;

    // Leasehold Fee (only if leasehold)
    const leaseholdFeeNet = ownershipType.toLowerCase() === 'leasehold' ? Number(useLegalFeeData.leasehold_fee || 0) : 0;
    const leaseholdFeeVAT = leaseholdFeeNet * 0.20;
    const leaseholdFeeGross = leaseholdFeeNet + leaseholdFeeVAT;

    // TT Fee
    const ttFeeNet = Number(useLegalFeeData.tt_fee || 0);
    const ttFeeVAT = ttFeeNet * 0.20;
    const ttFeeGross = ttFeeNet + ttFeeVAT;

    // Identity Check
    const identityCheckNet = Number(useLegalFeeData.identity_check || 0);
    const identityCheckVAT = 0; // No VAT on identity check
    const identityCheckGross = identityCheckNet;

    // Official Copies (disbursement)
    const officialCopiesNet = Number(useLegalFeeData.official_copies || 0);
    const officialCopiesVAT = 0;
    const officialCopiesGross = officialCopiesNet;

    // Calculate totals
    const legalCostNet = legalFeeNet + leaseholdFeeNet + ttFeeNet + identityCheckNet;
    const legalCostVAT = legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + identityCheckVAT;
    const legalCostGross = legalCostNet + legalCostVAT;

    const distributionCostNet = officialCopiesNet;
    const distributionCostVAT = officialCopiesVAT;
    const distributionCostGross = distributionCostNet + distributionCostVAT;

    const totalNet = legalCostNet + distributionCostNet;
    const totalVAT = legalCostVAT + distributionCostVAT;
    const totalGross = totalNet + totalVAT;

    // Build HTML table
    let tableHTML = `
      <table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-family: Arial, sans-serif;">
        <thead>
          <tr style="background-color: #6f42c1; color: white;">
            <th style="border: 1px solid #dee2e6; padding: 12px; text-align: left;">Description</th>
            <th style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">Net</th>
            <th style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">VAT</th>
            <th style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">Gross</th>
          </tr>
        </thead>
        <tbody>
          <!-- Legal Cost Section -->
          <tr style="background-color: #f8f9fa;">
            <td colspan="4" style="border: 1px solid #dee2e6; padding: 10px; font-weight: 600;">Legal Cost</td>
          </tr>
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Legal Fees</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalFeeNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalFeeVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalFeeGross)}</td>
          </tr>
    `;

    // Add Leasehold Fee if applicable
    if (leaseholdFeeNet > 0) {
      tableHTML += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Leasehold Fee</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(leaseholdFeeNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(leaseholdFeeVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(leaseholdFeeGross)}</td>
          </tr>
      `;
    }

    // TT Fee
    tableHTML += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">TT Fee</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(ttFeeNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(ttFeeVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(ttFeeGross)}</td>
          </tr>
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Identity Check</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(identityCheckNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(identityCheckVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(identityCheckGross)}</td>
          </tr>
          <!-- Legal Cost Subtotal -->
          <tr style="background-color: #e9ecef; font-weight: 600;">
            <td style="border: 1px solid #dee2e6; padding: 12px;">Legal Cost Sub-Total</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalCostNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalCostVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalCostGross)}</td>
          </tr>
          <!-- Disbursements Section -->
          <tr style="background-color: #f8f9fa;">
            <td colspan="4" style="border: 1px solid #dee2e6; padding: 10px; font-weight: 600;">Disbursements</td>
          </tr>
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Official Copies</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(officialCopiesNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(officialCopiesVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(officialCopiesGross)}</td>
          </tr>
          <!-- Disbursements Subtotal -->
          <tr style="background-color: #e9ecef; font-weight: 600;">
            <td style="border: 1px solid #dee2e6; padding: 12px;">Disbursements Sub-Total</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(distributionCostNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(distributionCostVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(distributionCostGross)}</td>
          </tr>
          <!-- Grand Total -->
          <tr style="background-color: #6f42c1; color: white; font-weight: 700; font-size: 1.1em;">
            <td style="border: 1px solid #dee2e6; padding: 12px;">Total</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(totalNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(totalVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(totalGross)}</td>
          </tr>
        </tbody>
      </table>
    `;

    return tableHTML;
  };

  const handleViewEmailTemplate = (email) => {
    // Replace placeholders with actual data
    let content = email.content || '';

    // Get customer data
    const customer = leadData?.customers?.[0] || {};
    const fullName = `${customer.forename || ''} ${customer.surname || ''}`.trim() || 'Customer';

    // Format current date
    const currentDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Get property address
    let propertyAddress = leadData?.property_address || leadData?.address || '';
    if (typeof propertyAddress === 'object') {
      const parts = [
        propertyAddress.address1 || propertyAddress.line1,
        propertyAddress.address2 || propertyAddress.line2,
        propertyAddress.city || propertyAddress.town,
        propertyAddress.county || propertyAddress.country,
        propertyAddress.postcode
      ].filter(Boolean);
      propertyAddress = parts.join(', ');
    }

    // Format property price
    const propertyPrice = leadData?.property_value ? `£${Number(leadData.property_value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '';

    // Replace all placeholders (both single and double brace)
    const replacements = {
      'customer_title': customer.title || '',
      'customer_forename': customer.forename || '',
      'customer_surname': customer.surname || '',
      'customer_email': customer.email || '',
      'customer_phone': customer.phone_number || '',
      'customer_name': fullName,
      'client_name': fullName,
      'client': fullName,
      'ClientName': fullName,
      'date': currentDate,
      'reference': leadData?.lead_id || '',
      'lead_id': leadData?.lead_id || '',
      'property': propertyAddress,
      'property_address': propertyAddress,
      'price': propertyPrice,
      'property_value': propertyPrice,
      'tenure': leadData?.ownership_type || 'Freehold',
      'branch_name': leadData?.branch_id?.branchname || leadData?.branch_id?.name || 'My Law Matters',
      'branch_phone': leadData?.branch_id?.phone || '',
      'branch_email': leadData?.branch_id?.email || ''
    };

    Object.entries(replacements).forEach(([key, value]) => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
      content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    });

    // Replace URL placeholders
    const portalUrl = CUSTOMER_PORTAL_URL;
    ['LeadInstructUrl', 'AutoLoginUrl', 'confirmation_link', 'customer_portal_link'].forEach(key => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), portalUrl);
      content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), portalUrl);
    });

    // Convert markdown links to HTML
    content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" style="color: #667eea;">$1</a>');

    // Generate and replace fee_table placeholder
    const feeTable = generateMortgageRedemptionFeeTable(leadData, legalFeeData);
    content = content.replace(/\{\{fee_table\}\}/g, feeTable);
    content = content.replace(/\{fee_table\}/g, feeTable);
    content = content.replace(/\{\{customer_fee\}\}/g, feeTable);
    content = content.replace(/\{customer_fee\}/g, feeTable);

    setSelectedEmail({ ...email, content });
    setViewEmailModal(true);
  };

  const handleSendEmail = async (templateId, templateName) => {
    try {
      setSendingEmail(true);

      const result = await Swal.fire({
        title: 'Send Email',
        text: `Are you sure you want to send "${templateName}" to the customer?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Send',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) {
        setSendingEmail(false);
        return;
      }

      const response = await postRequest(
        'private/sendEmailFromTemplate',
        {
          lead_id: lead_id,
          template_id: templateId
        },
        authCtx.token
      );

      if (response?.status === 200 || response?.status === 'success') {
        Swal.fire('Success', 'Email sent successfully', 'success');
        fetchEmailLogs(); // Refresh email logs
      } else {
        throw new Error(response?.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire('Error', error?.message || 'Failed to send email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  // Auto-fetch email templates and logs when leadData is available
  useEffect(() => {
    if (leadData && authCtx.token) {
      fetchEmailTemplates();
      fetchEmailLogs();
    }
  }, [leadData, authCtx.token]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!leadData) {
    return (
      <div className="text-center mt-5">
        <p>No lead data found</p>
      </div>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `Mortgage Redemption #${lead_id}`, path: '' },
        ]}
      />
      <br />

      <div className='page-header pb-3'>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className='mt-0 mb-2'>
              Conveyancing (Mortgage Redemption) #{leadData?.lead_id || lead_id}
            </h1>
          </div>
        </div>
      </div>

      <div className='page-top-formview mb-3'>
        <div className='customer-tab-view leads-tab-view'>
          <Tabs defaultActiveKey="overview" id="mortgage-redemption-tabs">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <div className='mt-3'>
                {/* Quote Summary Box */}
                <div className="card shadow-sm mb-4" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <div className="card-header" style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    padding: '1rem 1.25rem'
                  }}>
                    <h5 className="mb-0" style={{ color: '#495057', fontWeight: '600' }}>
                      Quote Summary
                    </h5>
                  </div>
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-baseline gap-3">
                        <span className="text-muted small">Total Cost</span>
                        {loadingFee ? (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            Loading...
                          </span>
                        ) : quoteFeeData ? (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            £{quoteFeeData.net.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Net + £{quoteFeeData.vat.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VAT = £{quoteFeeData.gross.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Gross
                          </span>
                        ) : (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            £{((leadData?.net_value || leadData?.legal_fee || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Net + £{((leadData?.vat_amount || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VAT = £{(leadData?.gross_value || leadData?.total_quote || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Gross
                          </span>
                        )}
                      </div>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowQuoteModal(true);
                          fetchQuoteFee();
                        }}
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        View Quote Detail
                      </a>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-secondary me-2">
                          <i className="fas fa-building me-1"></i>
                          {leadData?.branch_name || 'Manchester'}
                        </span>
                        <span className="badge bg-info">{leadData?.created_by || localStorage.getItem('username') || 'User'}</span>
                      </div>
                      <div className="text-muted small">
                        {(() => {
                          const date = leadData?.quote_date || leadData?.createdAt || leadData?.created_at;
                          if (!date) return 'Date not available';
                          const dateObj = new Date(date);
                          return `${dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="card-footer bg-light" style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.25rem' }}>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <Button
                        variant="contained"
                        className='text-capitalize'
                        onClick={(e) => {
                          e.preventDefault();
                          console.log('🚀 Revise Quote clicked for Mortgage Redemption');
                          setReviseModalShow(true);
                        }}
                        style={{
                          backgroundColor: '#dc3545',
                          borderColor: '#dc3545',
                          fontSize: '0.875rem',
                          padding: '8px 16px'
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" style={{ marginRight: '6px' }} />
                        Revise Quote
                      </Button>
                      <Button
                        variant="outlined"
                        className='text-capitalize'
                        onClick={handleRegenerateQuote}
                        style={{
                          color: '#6c757d',
                          borderColor: '#6c757d',
                          fontSize: '0.875rem',
                          padding: '8px 16px'
                        }}
                      >
                        <RefreshIcon fontSize="small" style={{ marginRight: '6px' }} />
                        Regenerate Quote
                      </Button>
                    </div>
                  </div>
                </div>

                <ul className="list-group mb-3">
                  {/* Address */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Address</div>
                      <div className="col-8">
                        {(() => {
                          // Try multiple locations for address
                          const address = leadData?.propertyDetails?.address
                            || leadData?.property_address
                            || leadData?.address
                            || leadData?.conveyancing_details?.property_address
                            || leadData?.conveyancing_details?.propertyDetails?.address;

                          console.log('🏠 Checking address:', address);

                          if (!address) return <span className="text-muted">No address specified</span>;

                          if (typeof address === 'string') {
                            return address;
                          }

                          if (typeof address === 'object') {
                            const parts = [];
                            if (address.line1 || address.address1) parts.push(address.line1 || address.address1);
                            if (address.line2 || address.address2) parts.push(address.line2 || address.address2);
                            if (address.town) parts.push(address.town);
                            if (address.county) parts.push(address.county);
                            if (address.postcode) parts.push(address.postcode);
                            return parts.length > 0 ? parts.join(', ') : <span className="text-muted">No address specified</span>;
                          }

                          return <span className="text-muted">No address specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Property Value */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Property Value</div>
                      <div className="col-8">
                        {(() => {
                          const propertyValue = leadData?.propertyDetails?.propertyValue
                            || leadData?.propertyDetails?.property_value
                            || leadData?.property_value
                            || leadData?.conveyancing_details?.property_value
                            || leadData?.conveyancing_details?.propertyDetails?.propertyValue
                            || 0;

                          console.log('💰 Checking property value:', propertyValue);

                          return propertyValue > 0
                            ? `£${propertyValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : <span className="text-muted">Not specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Customers */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Customers</div>
                      <div className="col-8">
                        {(() => {
                          console.log('🎨 Rendering customers UI');
                          console.log('👥 Customer Details State:', customerDetails);
                          console.log('👥 Lead Data Customers:', leadData?.customers);

                          // Check if leadData has customers
                          if (!leadData?.customers || leadData.customers.length === 0) {
                            console.log('⚠️ No customers in lead data');
                            return <span className="text-muted">No customers</span>;
                          }

                          // Check if customer details are still loading
                          if (customerDetails.length === 0) {
                            console.log('⏳ Customer details still loading');
                            return <span className="text-muted">Loading customers...</span>;
                          }

                          console.log('✅ Rendering customer names');
                          return customerDetails.map((customer, index) => {
                            console.log(`🎨 Rendering customer ${index}:`, customer);
                            const name = `${customer?.title || ''} ${customer?.forename || ''} ${customer?.surname || ''}`.trim();

                            if (!name) {
                              console.log('⚠️ Customer has no name');
                              return (
                                <div key={index}>
                                  <span className="text-muted">Unknown Customer</span>
                                </div>
                              );
                            }

                            console.log(`✅ Displaying name: ${name}`);
                            return (
                              <div key={index}>
                                {name}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Ownership Type */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Ownership Type</div>
                      <div className="col-8">
                        {(() => {
                          // Priority: info.ownershipType (new field) > ownership_type (old field)
                          const ownershipType = leadData?.info?.ownershipType
                            || leadData?.ownership_type
                            || leadData?.conveyancing_details?.ownership_type
                            || 'Freehold';

                          console.log('🏘️ Checking ownership type:', ownershipType);

                          // Capitalize first letter for display
                          return ownershipType.charAt(0).toUpperCase() + ownershipType.slice(1);
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Lender Panel */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Lender Panel</div>
                      <div className="col-8">
                        {(() => {
                          const lenderPanelId = leadData?.lender_panel_id;

                          console.log('🏦 Checking lender panel:', {
                            lenderPanelId,
                            type: typeof lenderPanelId,
                            lenderPanels: lenderPanels.length
                          });

                          if (!lenderPanelId) {
                            return <span className="text-muted">Not specified</span>;
                          }

                          // If it's already populated object with name
                          if (typeof lenderPanelId === 'object' && lenderPanelId.name) {
                            console.log('✅ Found lender panel name (object):', lenderPanelId.name);
                            return (
                              <div>
                                <div style={{ color: '#495057' }}>
                                  {lenderPanelId.name}
                                </div>
                                {lenderPanelId?.lender_numbers && lenderPanelId.lender_numbers.length > 0 && (
                                  <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                    {lenderPanelId.lender_numbers.join(', ')}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // If it's an ID (string), find in lenderPanels array
                          if (typeof lenderPanelId === 'string') {
                            const matchedPanel = lenderPanels.find(panel => panel._id === lenderPanelId);
                            if (matchedPanel) {
                              console.log('✅ Found lender panel name (by ID):', matchedPanel.name);
                              return (
                                <div>
                                  <div style={{ color: '#495057' }}>
                                    {matchedPanel.name}
                                  </div>
                                  {matchedPanel?.lender_numbers && matchedPanel.lender_numbers.length > 0 && (
                                    <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                      {matchedPanel.lender_numbers.join(', ')}
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              console.log('❌ Lender panel ID not found in lenderPanels array');
                              return <span className="text-muted">{lenderPanelId}</span>;
                            }
                          }

                          return <span className="text-muted">Not specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Partner */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Partner</div>
                      <div className="col-8">
                        {leadData?.partner_id?.name || <span className="text-muted">Not specified</span>}
                      </div>
                    </div>
                  </li>

                  {/* Group Code */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Group Code</div>
                      <div className="col-8">
                        {(() => {
                          const groupCode = leadData?.group_code;

                          if (groupCode && typeof groupCode === 'object') {
                            return (
                              <span>
                                {groupCode.name} ({groupCode.code})
                              </span>
                            );
                          }

                          if (leadData?.group_code_code) {
                            return leadData.group_code_code;
                          }

                          return <span className="text-muted">Not specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Status */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Status</div>
                      <div className="col-8">
                        {leadData?.status || leadData?.lead_status || 'Pending'}
                      </div>
                    </div>
                  </li>
                </ul>

                {/* Edit Button and More Actions */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        console.log('Edit button clicked - Mortgage Redemption');

                        const firstCustomerId = leadData?.customers && Array.isArray(leadData.customers) && leadData.customers.length > 0
                          ? (leadData.customers[0].customer_id || leadData.customers[0]._id || leadData.customers[0])
                          : null;
                        const customerParam = firstCustomerId ? `?customerId=${firstCustomerId}` : '';

                        navigate(`/conveyancing/mortgage-redemption/${leadData?.lead_id || lead_id}${customerParam}`);
                      }}
                      style={{
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" style={{ marginRight: '4px' }} />
                      Edit
                    </Button>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                      Created: {leadData?.createdAt ? new Date(leadData.createdAt).toLocaleDateString('en-GB') : 'Date not available'} - {leadData?.created_by || localStorage.getItem('username') || 'Unknown'}
                    </span>

                    <Dropdown drop="up">
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        style={{
                          borderColor: '#6c757d',
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}
                      >
                        More Actions
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changestatus', {
                              state: {
                                leadId: lead_id,
                                currentStatus: leadData?.status,
                                returnUrl: window.location.pathname
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Status
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changegroupcode', {
                              state: {
                                id: lead_id,
                                title: slugify(leadData?.product_title),
                                returnUrl: `/leads/view/mortgage-redemption/${lead_id}`
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Group Code
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handleDeleteLead} style={{ color: '#dc3545' }}>
                          <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Delete Lead
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </Tab>

            {/* QUOTES TAB */}
            <Tab eventKey="quotes" title="Quotes">
              <div className="mt-3">
                {/* Current Quote Card */}
                {leadData && legalFeeData && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="mb-1">
                            £{(() => {
                              const legalFee = Number(legalFeeData.legal_fee || 0);
                              const leaseholdFee = Number(legalFeeData.leasehold_fee || 0);
                              const ttFee = Number(legalFeeData.tt_fee || 0);
                              const identityCheck = Number(legalFeeData.identity_check || 0);
                              const officialCopies = Number(legalFeeData.official_copies || 0);

                              const totalNet = legalFee + leaseholdFee + ttFee + identityCheck + officialCopies;
                              const legalFeeVAT = legalFee * 0.20;
                              const leaseholdFeeVAT = leaseholdFee * 0.20;
                              const ttFeeVAT = ttFee * 0.20;
                              const identityCheckVAT = identityCheck * 0.20;
                              const totalVAT = legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + identityCheckVAT;
                              const totalGross = totalNet + totalVAT;

                              return totalGross.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            })()}
                            {' '}
                            <span className="text-muted" style={{fontSize: '0.875rem'}}>
                              (£{Number(legalFeeData.legal_fee || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + £{(() => {
                                const legalFeeVAT = Number(legalFeeData.legal_fee || 0) * 0.20;
                                const leaseholdFeeVAT = Number(legalFeeData.leasehold_fee || 0) * 0.20;
                                const ttFeeVAT = Number(legalFeeData.tt_fee || 0) * 0.20;
                                const identityCheckVAT = Number(legalFeeData.identity_check || 0) * 0.20;
                                return (legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + identityCheckVAT).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()})
                            </span>
                          </h5>
                          <p className="text-muted mb-0">
                            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {legalFeeData?.branch_name ? (
                            <span className="badge bg-secondary">{legalFeeData.branch_name}</span>
                          ) : (
                            <span className="badge bg-secondary">Partner rate card</span>
                          )}
                          <span className="badge bg-primary">{localStorage.getItem('username') || leadData?.created_by || 'User'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quote History Section - Enhanced UI */}
                {quoteHistory && quoteHistory.length > 0 && (
                  <div className="quote-history-section">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                        <i className="fas fa-history me-2" style={{ color: '#3498db' }}></i>
                        Quote History
                      </h5>
                      <span className="badge bg-primary" style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                        {quoteHistory.length} {quoteHistory.length === 1 ? 'Version' : 'Versions'}
                      </span>
                    </div>

                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                      {/* Timeline line */}
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '20px',
                        bottom: '20px',
                        width: '2px',
                        backgroundColor: '#e0e0e0'
                      }}></div>

                      {quoteHistory.map((quote, index) => {
                        const totalGross = quote.totals?.total?.gross || 0;
                        const totalNet = quote.totals?.total?.net || 0;
                        const totalVat = quote.totals?.total?.vat || 0;
                        const legalCostGross = quote.totals?.legalCost?.gross || 0;
                        const isActive = quote.is_active || false;
                        const revisionNumber = quote.revision_number || 1;
                        const source = quote.calculation_source || 'api';

                        // Use revision_label directly from API
                        const versionLabel = quote.revision_label || `Revision ${revisionNumber}`;

                        // Format date
                        const displayDate = quote.revised_at || quote.createdAt;

                        // Icon and color based on type
                        let iconClass = 'fa-file-invoice';
                        let iconColor = '#95a5a6';
                        let borderColor = '#e0e0e0';

                        if (isActive) {
                          iconClass = 'fa-check-circle';
                          iconColor = '#27ae60';
                          borderColor = '#27ae60';
                        } else if (versionLabel === 'Original') {
                          iconClass = 'fa-star';
                          iconColor = '#f39c12';
                        } else if (source === 'manual_revision') {
                          iconClass = 'fa-edit';
                          iconColor = '#3498db';
                        }

                        return (
                          <div key={quote._id || index} style={{ position: 'relative', marginBottom: '24px' }}>
                            {/* Timeline dot */}
                            <div style={{
                              position: 'absolute',
                              left: '-26px',
                              top: '16px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: iconColor,
                              border: '3px solid white',
                              boxShadow: '0 0 0 2px ' + iconColor,
                              zIndex: 1
                            }}></div>

                            {/* Card */}
                            <div className="card" style={{
                              border: `2px solid ${isActive ? borderColor : '#e9ecef'}`,
                              borderRadius: '12px',
                              boxShadow: isActive ? '0 4px 12px rgba(39, 174, 96, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease',
                              backgroundColor: isActive ? '#f8fff9' : 'white'
                            }}>
                              <div className="card-body" style={{ padding: '20px' }}>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  {/* Left: Version & Amount */}
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '18px' }}></i>
                                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                                        {versionLabel}
                                      </h5>
                                      {isActive && (
                                        <span className="badge" style={{
                                          backgroundColor: '#27ae60',
                                          color: 'white',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}>
                                          CURRENT
                                        </span>
                                      )}
                                    </div>

                                    {/* Date & Time */}
                                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="far fa-clock me-1"></i>
                                      {new Date(displayDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })} at {new Date(displayDate).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>

                                    {/* Source Badge */}
                                    <span className="badge" style={{
                                      backgroundColor: source === 'manual_revision' ? '#3498db' : '#95a5a6',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem'
                                    }}>
                                      {source === 'manual_revision' ? 'MANUAL REVISION' :
                                       source === 'api' ? 'AUTO CALCULATED' :
                                       source.toUpperCase()}
                                    </span>
                                  </div>

                                  {/* Right: Branch & Action */}
                                  <div className="d-flex flex-column align-items-end gap-2">
                                    {quote.branch_name && (
                                      <span className="badge" style={{
                                        backgroundColor: '#e8f4f8',
                                        color: '#3498db',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}>
                                        <i className="fas fa-building me-1"></i>
                                        {quote.branch_name}
                                      </span>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      style={{
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        borderWidth: '2px'
                                      }}
                                      onClick={() => {
                                        console.log('🔍 View Quote clicked for:', versionLabel);
                                        console.log('📦 Quote data:', quote);

                                        // Transform NESTED format to FLAT format for ViewQuoteDetailModal
                                        const fees = quote.fees || {};
                                        const disbursements = quote.disbursements || {};

                                        // Convert Map to Object if needed
                                        const feesObj = fees instanceof Map ? Object.fromEntries(fees) : fees;
                                        const disbursementsObj = disbursements instanceof Map ? Object.fromEntries(disbursements) : disbursements;

                                        console.log('🔍 Quote fees:', feesObj);
                                        console.log('🔍 Quote disbursements:', disbursementsObj);
                                        console.log('🔍 Quote totals:', quote.totals);

                                        // Calculate totals
                                        const totalVat = quote.totals?.total?.vat || 0;
                                        const totalGross = quote.totals?.total?.gross || 0;

                                        const transformedFeeData = {
                                          branch_name: quote.branch_name,
                                          service_type: quote.service_type || 'Mortgage_Redemption',
                                          legal_fee: feesObj.legalFee?.amount || 0,
                                          tt_fee: feesObj.ttFee?.amount || feesObj.telegraphicTransferFee?.amount || 0,
                                          identity_check: feesObj.identityCheck?.amount || 0,
                                          leasehold_fee: feesObj.leaseholdFee?.amount || 0,
                                          official_copies: disbursementsObj.officialCopies?.amount || 0,
                                          vat_amount: totalVat,
                                          total_quote: totalGross,
                                          all_fees: feesObj,
                                          all_disbursements: disbursementsObj,
                                          totals: quote.totals
                                        };

                                        console.log('✅ Transformed fee data:', transformedFeeData);

                                        setSelectedQuote({
                                          legalFeeData: transformedFeeData,
                                          leadData: leadData,
                                          revision_label: versionLabel,
                                          is_history: !isActive
                                        });
                                        setShowQuoteDetailModal(true);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" style={{ fontSize: '14px', marginRight: '4px' }} />
                                      View Details
                                    </Button>
                                  </div>
                                </div>

                                {/* Pricing Breakdown */}
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                  <div className="row g-3">
                                    {/* Total Amount - Highlighted */}
                                    <div className="col-md-4">
                                      <div style={{
                                        backgroundColor: isActive ? '#e8f5e9' : '#f8f9fa',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                      }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                          Total Amount
                                        </p>
                                        <h4 className="mb-0" style={{ color: isActive ? '#27ae60' : '#2c3e50', fontWeight: 700 }}>
                                          £{Number(totalGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h4>
                                      </div>
                                    </div>

                                    {/* Legal Costs */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          <i className="fas fa-balance-scale me-1"></i>
                                          Legal Costs
                                        </p>
                                        <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                          £{Number(legalCostGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h6>
                                      </div>
                                    </div>

                                    {/* VAT */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          <i className="fas fa-receipt me-1"></i>
                                          VAT Included
                                        </p>
                                        <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                          £{Number(totalVat).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h6>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* No quotes message */}
                {(!leadData || !legalFeeData) && (!quoteHistory || quoteHistory.length === 0) && (
                  <div className="text-center py-5">
                    <i className="fas fa-file-invoice fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Quotes Available</h5>
                    <p className="text-muted">No quotes have been generated yet.</p>
                  </div>
                )}
              </div>
            </Tab>

            {/* NOTES TAB */}
            <Tab eventKey="notes" title="Notes">
              <Notes lead_id={lead_id} product_id={leadData?.product_id} />
            </Tab>

            {/* COMMUNICATION TAB */}
            <Tab eventKey="communication" title="Communication">
              <div className='communication-table'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                      Contact Attempt ({contactAttempts})
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('increase')}>
                        <MailOutlineIcon fontSize="small" /> &nbsp; Increase Contact Attempt Count
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('reset')}>
                        <CallOutlinedIcon fontSize="small" /> &nbsp; Reset
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Send Email Dropdown - Right Aligned */}
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="send-email-dropdown" disabled={sendingEmail}>
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {emailTemplates.length === 0 ? (
                          <Dropdown.Item disabled>No templates available (Count: {emailTemplates.length})</Dropdown.Item>
                        ) : (
                          emailTemplates.map((template) => (
                            <Dropdown.Item
                              key={template._id}
                              onClick={() => handleSendEmail(template._id, template.name)}
                            >
                              {template.name}
                            </Dropdown.Item>
                          ))
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Refresh Button for debugging */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Manual refresh triggered');
                        fetchEmailTemplates();
                      }}
                      title="Refresh templates"
                    >
                      <RefreshIcon fontSize="small" />
                    </Button>
                  </div>
                </div>

                {/* Email History Table */}
                <div className="mt-4">
                  <Table striped responsive bordered>
                    <thead>
                      <tr>
                        <th>Sent</th>
                        <th>Customer</th>
                        <th>Content</th>
                        <th style={{ width: '80px', textAlign: 'center' }}>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingEmails ? (
                        <tr>
                          <td colSpan="4" className="text-center">Loading...</td>
                        </tr>
                      ) : emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">No communication logs found</td>
                        </tr>
                      ) : (
                        emailLogs.map((log, index) => (
                          <tr key={log._id || index}>
                            <td>
                              {log.sent_at ? new Date(log.sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : (log.isAutomation ? <span className="text-muted">Not triggered</span> : 'N/A')}
                            </td>
                            <td>
                              <div>
                                {log.recipient_name || log.customer_id?.forename || 'Unknown'}
                                <div className="text-muted small">{log.recipient_email}</div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{log.template_name || log.subject || 'No title'}</strong>
                                <div className="text-muted small">
                                  {log.subject && log.subject !== log.template_name && (
                                    <span>Subject: {log.subject}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewEmailTemplate(log)}
                                title="View email content"
                                sx={{ minWidth: '40px', padding: '4px 8px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                <div className="text-center py-5">
                  <h5 className="text-muted">Tasks Functionality Coming Soon</h5>
                  <p className="text-muted">Task management features will be available here.</p>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      {/* View Quote Modal */}
      <Modal show={showQuoteModal} onHide={() => setShowQuoteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Quote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFee ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading quote details...</p>
            </div>
          ) : quoteFeeData ? (
            <>
              <Table bordered hover style={{ marginBottom: '0' }}>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th style={{ fontWeight: '600', width: '40%' }}>MORTGAGE REDEMPTION</th>
                    <th style={{ fontWeight: '600', textAlign: 'center' }}>NET</th>
                    <th style={{ fontWeight: '600', textAlign: 'center' }}>VAT</th>
                    <th style={{ fontWeight: '600', textAlign: 'center' }}>GROSS</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Legal Cost Section Header */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Legal Cost</strong></td>
                  </tr>

                  {/* Legal Fee */}
                  {quoteFeeData.fees?.legalFee && (
                    <tr>
                      <td>Legal Fee</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.legalFee.amount.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.legalFee.vat.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.legalFee.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* Telegraphic Transfer Fee */}
                  {quoteFeeData.fees?.telegraphicTransferFee && (
                    <tr>
                      <td>Telegraphic Transfer (TT) Fee</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.telegraphicTransferFee.amount.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.telegraphicTransferFee.vat.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.telegraphicTransferFee.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* Identity Check */}
                  {quoteFeeData.fees?.identityCheck && (
                    <tr>
                      <td>Identity Check</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.identityCheck.amount.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.identityCheck.vat.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.identityCheck.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* Leasehold Fee */}
                  {quoteFeeData.fees?.leaseholdFee && (
                    <tr>
                      <td>Leasehold Fee</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.leaseholdFee.amount.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.leaseholdFee.vat.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.fees.leaseholdFee.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* Legal Cost Subtotal */}
                  <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                    <td>TOTAL LEGAL COST</td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.amount || 0) +
                        (quoteFeeData.fees?.telegraphicTransferFee?.amount || 0) +
                        (quoteFeeData.fees?.identityCheck?.amount || 0) +
                        (quoteFeeData.fees?.leaseholdFee?.amount || 0)
                      ).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.vat || 0) +
                        (quoteFeeData.fees?.telegraphicTransferFee?.vat || 0) +
                        (quoteFeeData.fees?.identityCheck?.vat || 0) +
                        (quoteFeeData.fees?.leaseholdFee?.vat || 0)
                      ).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.gross || 0) +
                        (quoteFeeData.fees?.telegraphicTransferFee?.gross || 0) +
                        (quoteFeeData.fees?.identityCheck?.gross || 0) +
                        (quoteFeeData.fees?.leaseholdFee?.gross || 0)
                      ).toFixed(2)}
                    </td>
                  </tr>

                  {/* Disbursements Section Header */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Disbursement - Third Party Expenditure</strong></td>
                  </tr>

                  {/* Official Copies */}
                  {quoteFeeData.disbursements?.officialCopies && (
                    <tr>
                      <td>Official Copies</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.disbursements.officialCopies.amount.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.disbursements.officialCopies.vat.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>£{quoteFeeData.disbursements.officialCopies.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* Disbursements Subtotal */}
                  <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                    <td>TOTAL DISBURSEMENT COST</td>
                    <td style={{ textAlign: 'center' }}>
                      £{(quoteFeeData.disbursements?.officialCopies?.amount || 0).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(quoteFeeData.disbursements?.officialCopies?.vat || 0).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(quoteFeeData.disbursements?.officialCopies?.gross || 0).toFixed(2)}
                    </td>
                  </tr>

                  {/* Grand Total */}
                  <tr style={{ backgroundColor: '#d1ecf1', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <td>TOTAL (Legal Cost + Disbursement Cost)</td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.amount || 0) +
                        (quoteFeeData.fees?.telegraphicTransferFee?.amount || 0) +
                        (quoteFeeData.fees?.identityCheck?.amount || 0) +
                        (quoteFeeData.fees?.leaseholdFee?.amount || 0) +
                        (quoteFeeData.disbursements?.officialCopies?.amount || 0)
                      ).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.vat || 0) +
                        (quoteFeeData.fees?.telegraphicTransferFee?.vat || 0) +
                        (quoteFeeData.fees?.identityCheck?.vat || 0) +
                        (quoteFeeData.fees?.leaseholdFee?.vat || 0) +
                        (quoteFeeData.disbursements?.officialCopies?.vat || 0)
                      ).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.gross || 0) +
                        (quoteFeeData.fees?.telegraphicTransferFee?.gross || 0) +
                        (quoteFeeData.fees?.identityCheck?.gross || 0) +
                        (quoteFeeData.fees?.leaseholdFee?.gross || 0) +
                        (quoteFeeData.disbursements?.officialCopies?.gross || 0)
                      ).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No quote data available</p>
            </div>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outlined"
              size="small"
              onClick={downloadQuotePDF}
              disabled={!quoteFeeData}
              style={{
                borderColor: '#6c757d',
                color: '#6c757d',
                textTransform: 'none'
              }}
            >
              <i className="fas fa-download me-2"></i>
              Download
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* View Email Modal */}
      <Modal show={viewEmailModal} onHide={() => setViewEmailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedEmail?.template_name || 'Email Preview'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedEmail && selectedEmail.content && (
            <div
              dangerouslySetInnerHTML={{ __html: selectedEmail.content }}
              style={{
                border: '1px solid #dee2e6',
                padding: '20px',
                borderRadius: '4px',
                backgroundColor: '#fff'
              }}
            />
          )}
          {selectedEmail && !selectedEmail.content && (
            <div>
              <div className="mb-3">
                <strong>Template Name:</strong>
                <p>{selectedEmail.template_name || 'N/A'}</p>
              </div>
              <div className="mb-3">
                <strong>Sent Date:</strong>
                <p>
                  {selectedEmail.sent_date
                    ? new Date(selectedEmail.sent_date).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'N/A'}
                </p>
              </div>
              <div className="mb-3">
                <strong>Status:</strong>
                <p>
                  <span
                    className={`badge ${
                      selectedEmail.status === 'sent' || selectedEmail.status === 'delivered'
                        ? 'bg-success'
                        : selectedEmail.status === 'failed'
                        ? 'bg-danger'
                        : 'bg-warning'
                    }`}
                  >
                    {selectedEmail.status || 'pending'}
                  </span>
                </p>
              </div>
              {selectedEmail.subject && (
                <div className="mb-3">
                  <strong>Subject:</strong>
                  <p>{selectedEmail.subject}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revise Quote Modal */}
      <ReviseQuoteModal
        show={reviseModalShow}
        onHide={() => setReviseModalShow(false)}
        quoteFeeData={quoteFeeData}
        leadData={leadData}
        authToken={authCtx.token}
        onReviseQuote={handleReviseQuote}
        legalFeeData={legalFeeData}
      />

      {/* View Quote Detail Modal */}
      <ViewQuoteDetailModal
        show={showQuoteDetailModal}
        onHide={() => {
          console.log('Closing ViewQuoteDetailModal');
          setShowQuoteDetailModal(false);
          setSelectedQuote(null);
        }}
        leadId={leadData?._id || lead_id}
        authToken={authCtx.token}
        legalFeeData={selectedQuote?.legalFeeData || legalFeeData}
        leadData={selectedQuote?.leadData || leadData}
        calculationTotals={selectedQuote?.legalFeeData?.totals || null}
      />
    </>
  );
};

export default ViewLeadMortgageRedemption;
